import { Barcode, Minus, Plus, Search } from 'reicon-react';
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import { notificar } from '@/lib/notificar';
import { Button } from '@/components/ui/button';
import { acaoBloqueavel, atributosDeBloqueio, type MotivoBloqueio } from '@/lib/bloqueio';
import { lerCentavosDigitados, lerDecimalDigitado } from '@/lib/numeroDigitado';
import { haJanelaAberta } from '@/lib/useFocoDeModal';
import { cn } from '@/lib/utils';
import {
  rotuloTipoCodigoProduto,
  type ConsultaDeProduto,
} from '../../domain/precificacao/codigoProduto';
import { ATRIBUTO_ATALHOS_PERMITIDOS } from '../../hotkeys/mapaAtalhos';
import {
  ZERO_CENTAVOS,
  calcularTotalLinha,
  formatarCentavos,
  somar,
  type Centavos,
} from '../../domain/precificacao/dinheiro';
import { preservarSelecaoNoProximoFoco } from '@/lib/selecionarConteudoAoFocar';
import {
  fecharTecladoVirtual,
  focarSemTeclado,
  reabrirTecladoNoModoAtual,
} from '@/lib/tecladoVirtual';
import {
  AVALIACAO_LIVRE,
  type AvaliacaoSaldo,
  type SaldoMilesimos,
} from '../../domain/estoque/saldoProduto';
import { TOTAL_MINIMO_DA_LINHA } from '../../domain/precificacao/linha';
import {
  MILESIMOS_POR_UNIDADE,
  formatarQuantidade,
  milesimos,
  milesimosDeUnidades,
  somarQuantidades,
  type Milesimos,
} from '../../domain/precificacao/quantidade';
import type { ItemConsolidado } from '../../domain/precificacao/descontoItem';
import { avisoDePrecoMinimo } from '../../stores/slices/carrinhoSlice';
import { useCenarioProdutoStore } from '../../stores/cenarioProdutoStore';
import { useEdicaoItemStore } from '../../stores/edicaoItemStore';
import { useFocoVendaStore } from '../../stores/focoVendaStore';
import { useJanelasStore } from '../../stores/janelasStore';
import { useVendaStore } from '../../stores/vendaStore';
// Mesma leitura que `AcoesFinaisVenda` (004) faz para travar o "Finalizar":
// o vendedor da venda tem um hook só, e duplicar o seletor aqui abriria duas
// respostas possíveis para "esta venda tem vendedor?".
import { useVendedorAtual } from '../vendedor/useVendedor';
import { ModalBuscaProduto } from './ModalBuscaProduto';
import { TEXTO_SEM_DESCONTO, useVinculoPrecoDesconto } from './useVinculoPrecoDesconto';
import {
  avaliarContraCarrinho,
  useContextoPrecificacao,
  useEdicaoDeItemExistente,
  useInsercaoDeProduto,
  useMotivoCarrinhoBloqueado,
  usePoliticaSaldo,
  type AjustesDaBarra,
  type ResultadoConfirmacao,
  type RevisaoProduto,
} from './useCarrinho';

const CENTAVOS_POR_REAL = 100;
/**
 * O rótulo "Quantidade" aponta para o input por `htmlFor`, não por
 * aninhamento — ver o comentário da célula de quantidade no JSX.
 */
const ID_CAMPO_QUANTIDADE = 'previa-campo-quantidade';
const UMA_UNIDADE = milesimos(MILESIMOS_POR_UNIDADE);
const QUANTIDADE_INICIAL = milesimosDeUnidades(1);

/**
 * Desconto que consome o item inteiro (pedido do usuário, 2026-09-04).
 *
 * A frase nomeia a saída — reduzir o desconto — porque o gesto que o operador
 * tentaria sozinho (confirmar assim mesmo) não funciona: o botão de inserir
 * fica bloqueado enquanto o total não voltar a `TOTAL_MINIMO_DA_LINHA`.
 */
const AVISO_DESCONTO_ZERA_ITEM =
  'O desconto não pode zerar o item: reduza o valor para o total ficar em pelo menos R$ 0,01.';

/**
 * Campos obrigatórios da prévia (pedido do usuário, 2026-09-04): sair de
 * quantidade ou preço zerados é erro, avisado na hora e com o foco devolvido ao
 * campo.
 *
 * **Zero é recusado em quantidade e preço, mas não em desconto** (decisão do
 * usuário na mesma data): um item sem desconto é o caso normal, e o campo
 * nasce em `0,00`; exigir um desconto positivo impediria a inserção mais
 * comum do caixa. O desconto grande demais continua coberto por
 * `AVISO_DESCONTO_ZERA_ITEM`.
 *
 * **Campo vazio vale zero** desde 2026-09-24 (pedido do usuário, regra de
 * `lib/numeroDigitado`): na quantidade e no preço ele cai na mesma recusa do
 * zero, com a mesma frase; no desconto ele **é** o zero, e sair do campo
 * apagado deixa de prender o foco — o campo só volta a mostrar `0,00`. Ali só
 * um texto que não é número bloqueia.
 */
const AVISO_QUANTIDADE_INVALIDA =
  'Informe a quantidade do item: ela precisa ser um número maior que zero.';
const AVISO_PRECO_INVALIDO =
  'Informe o preço unitário do item: ele precisa ser um valor maior que zero.';
const AVISO_DESCONTO_INVALIDO =
  'Desconto do item inválido: use apenas números, com no máximo duas casas decimais.';

/**
 * Código apagado com uma revisão na tela (correção do usuário, 2026-09-16).
 *
 * Desde que o campo de código voltou a ser alcançável (AD-240), dá para apagar
 * o que está nele e sair — e a prévia continuava exibindo unidade, preço,
 * desconto e total do produto **anterior**, agora sem código nenhum que os
 * justifique. O operador confirmaria um item que ele acredita ter cancelado.
 *
 * Por isso a saída **descarta a prévia inteira** em vez de só recusar o gesto:
 * sem código não existe item, e deixar os valores na tela seria manter na mão
 * do operador um item que ele não consegue nomear. O foco fica no campo, que é
 * onde o próximo código entra.
 */
const AVISO_CODIGO_OBRIGATORIO =
  'Código apagado: o item saiu da barra. Bipe ou digite o código do produto.';

/**
 * Venda sem vendedor não recebe produto (pedido do usuário, 2026-09-10).
 *
 * A trava por vendedor já existia no **fim** da venda — `AcoesFinaisVenda`
 * (004) recusa o "Finalizar" enquanto `vendedorAtual` é `null`, porque o ERP
 * não aceita NFCe sem vendedor associado (`FR-006`/`SC-003` da 012). Descobrir
 * isso só depois de o carrinho estar montado é tarde: o operador bipa a compra
 * inteira e só então é mandado escolher quem vende. Puxar a mesma exigência
 * para a primeira inserção põe o pedido no começo do fluxo, que é onde ele
 * custa um clique.
 *
 * A frase é irmã da que a 004 usa, e nomeia a saída — a lupa — porque é para
 * ela que o foco vai junto com o aviso (`focarVendedor`).
 */
const AVISO_SEM_VENDEDOR =
  'Escolha o vendedor da venda antes de inserir produtos: use a lupa ao lado de "Vendedor NFCe".';

function paraTextoDecimal(valorEmCentavos: number): string {
  return (valorEmCentavos / CENTAVOS_POR_REAL).toFixed(2).replace('.', ',');
}

/**
 * O que o campo de quantidade aceita digitar (correção do usuário, 2026-09-16):
 * dígitos e **um** separador decimal, `,` ou `.`. O que não for isso é
 * descartado no `onChange` — a tecla simplesmente não aparece.
 *
 * Filtrar em vez de recusar o campo inteiro: o operador digita rápido, com a
 * mão no teclado numérico do PDV, e apagar o que ele já digitou por causa de
 * uma tecla vizinha custaria mais que ignorá-la. O separador é preservado como
 * o operador o digitou — `lerQuantidadeTexto` trata os dois.
 */
function somenteQuantidade(texto: string): string {
  let separadorUsado = false;
  let saida = '';
  for (const caractere of texto) {
    if (caractere >= '0' && caractere <= '9') {
      saida += caractere;
      continue;
    }
    if ((caractere === ',' || caractere === '.') && !separadorUsado) {
      separadorUsado = true;
      saida += caractere;
    }
  }
  return saida;
}

/**
 * `"3"`, `"3,5"`, `",5"` ou `"3.5"` → `Milesimos`; inválida ou não positiva —
 * inclusive vazia, que vale zero (`lib/numeroDigitado`) — vira `null`.
 */
function lerQuantidadeTexto(texto: string): Milesimos | null {
  const unidades = lerDecimalDigitado(texto, 3);
  return unidades !== null && unidades > 0 ? milesimosDeUnidades(unidades) : null;
}

/**
 * Recusa a saída de um campo obrigatório: avisa o operador e devolve o foco ao
 * campo (pedido do usuário, 2026-09-04).
 *
 * O `.focus()` vai agendado (`setTimeout` de 0), não direto: quando a saída é
 * um TAB — ou um clique em outro controle — o navegador ainda move o foco
 * **depois** de o handler de `blur` rodar, e um foco pedido dentro dele seria
 * desfeito no mesmo gesto. Agendar para o fim da fila deixa a navegação
 * terminar e só então traz o foco de volta, que é o comportamento pedido
 * ("voltar o foco para o campo referente").
 *
 * O texto digitado permanece no campo, como no aviso de desconto que zera o
 * item: quem impede a inserção é `bloqueioDeInsercao`, e apagar o que o
 * operador escreveu tiraria dele a chance de só corrigir um dígito.
 */
function exigirCampo(campo: RefObject<HTMLInputElement | null>, aviso: string): void {
  notificar.erro(aviso);
  window.setTimeout(() => {
    campo.current?.focus();
    campo.current?.select();
  }, 0);
}

/**
 * "R$" à esquerda do valor, como o Pencil desenha no campo de desconto do item
 * (`design/CentriumCheckout.pen`, nó `qhuOQ` "Símbolo dinheiro desconto",
 * Inter 12/700 em `$body`): elemento próprio, **fora** do `<input>`.
 *
 * É o que faz o símbolo persistir na inserção e na edição (correção do
 * usuário, 2026-09-03) sem virar máscara: o `value` do campo continua sendo só
 * o número, então apagar, corrigir e digitar por cima seguem funcionando como
 * em qualquer input, o cursor nunca esbarra num prefixo protegido, e
 * `lerCentavos` recebe exatamente o mesmo texto de antes.
 */
function SimboloReal({ testId }: { testId: string }): ReactElement {
  return (
    <span className="shrink-0 text-sm font-bold text-muted-foreground" data-testid={testId}>
      R$
    </span>
  );
}

/**
 * Barra de entrada rápida de produto (T021, `CART-02`) — réplica fiel do frame
 * "Entrada rápida de produto" do Pencil (`design/CentriumCheckout.pen`,
 * confirmado via MCP do Pencil): **um único cartão sempre com todas as
 * células visíveis** (código, quantidade, unidade, preço, desconto, total,
 * nome do produto abaixo, botão de inserir) — nunca colapsa a só o campo de
 * código, mesmo sem nenhum produto resolvido ainda.
 *
 * Enter no campo de código é a tecla de **inserção rápida**: confirma a
 * entrada e insere direto, sem exibir revisão (produto pesável/simples/balança
 * — `'S'`/`'B'`/`''`). TAB é a tecla de **revisão**: carrega o produto via
 * `GetProduto` (`revisarPorCodigo`) e preenche todas as células com os dados
 * reais — mas só quando há código digitado; com o campo vazio TAB volta a ser
 * navegação e leva o foco à lupa de busca (pedido do usuário, 2026-09-04).
 *
 * Quantidade, preço e desconto são sempre `<input>` de verdade — não só
 * texto — para participarem da navegação por TAB. Unidade também é um
 * `<input>` (mesma célula do design), mas **`disabled`** (pedido do usuário,
 * 2026-09-03): vem do cadastro, o operador nunca pode alterá-la, e por isso
 * nem entra na navegação por TAB — o próprio navegador pula elementos
 * desabilitados. Preço e desconto só ficam editáveis quando
 * `ProdutoPesavelEditavel = 'E'` (`FR-014`); nos demais casos ficam
 * `disabled`, como a unidade — recusam o ponteiro e somem da navegação por
 * TAB (pedido do usuário, 2026-09-11, substituindo o `readOnly` de
 * 2026-09-03) —, e o foco ao resolver vai direto para o botão "+" (nada mais
 * a decidir). Em produto `'E'`, o foco ao resolver vai para a
 * **quantidade** — nunca para o botão de inserir — e o próximo TAB segue a
 * ordem natural do DOM (quantidade → preço → desconto → "+", pulando a
 * unidade desabilitada): o operador revisa e ajusta cada campo digitando,
 * sem precisar do mouse.
 *
 * Não registra atalho global de teclado: um `hotkey` de escopo de documento
 * competiria com a própria bipagem, que chega como digitação rápida neste input.
 *
 * Dono do modal de busca por termo livre (`ModalBuscaProduto`, T015,
 * `CART-01`): o modal é **só um seletor de código** — escolher um candidato
 * só devolve o `CodigoProduto` (`onProdutoSelecionado`), nunca resolve nem
 * insere nada sozinho. É esta barra que carrega o código escolhido no campo,
 * chama `GetProduto` e mostra a revisão — o mesmo caminho de TAB no código
 * digitado. Achado do usuário (2026-09-03): a revisão vivia por engano
 * dentro do modal, duplicando esta UI.
 *
 * Também é o destino do lápis de uma linha **já inserida** (`GridItens.tsx`,
 * `ListaItensMobile.tsx`) — correção do usuário, 2026-09-03: em vez de editar
 * só a quantidade inline, o lápis carrega a linha inteira aqui (via
 * `useEdicaoItemStore`), preservando quantidade/unidade/preço/desconto/total,
 * e só libera preço/desconto para edição quando `ProdutoPesavelEditavel = 'E'`
 * — em `'S'`/`'B'` (pesável) só a quantidade fica ajustável, mesma regra da
 * inserção; em `''` (não editável) o lápis fica desabilitado na origem.
 * Enquanto isso dura, esta barra e a linha de origem (que fica "vazia" na
 * grid, sem sumir de vez) ganham o mesmo contorno amarelo pulsante
 * (`cc-pulso-edicao`, `global.css`) — pedido do usuário, 2026-09-03: o
 * operador precisa reconhecer à distância que o item não foi cancelado, só
 * voltou pra cá pra revisão.
 *
 * **Três acertos visuais pedidos pelo usuário em 2026-09-03 (AD-136)** — os
 * três reaproximam a barra do desenho em vez de se afastarem dele:
 *
 * 1. Preço unitário e desconto do item passam a ficar **alinhados à
 *    esquerda**, como o total sempre esteve: no Pencil, os três frames de
 *    valor (`kwsQ0`, `BADmf`, `m9FeF`) centralizam só no eixo vertical e
 *    deixam o texto começar na borda esquerda — o `text-right` era invenção da
 *    implementação.
 * 2. O **"R$" persiste** enquanto o operador digita, via `SimboloReal`. O
 *    desenho já traz `R$ 11.824,12` no preço (`pFkT1`) e um "R$" próprio no
 *    desconto (`qhuOQ`); era só o modo editável que perdia o símbolo.
 * 3. O hover do "−" deixa de acender pelo campo inteiro (ver a célula de
 *    quantidade no JSX) e, como toda superfície `secondary`, agora **escurece**
 *    em vez de clarear (`--secondary-hover`, `global.css`).
 *
 * **Um desvio do Pencil** nesse último ponto: o desenho separa o "R$" do
 * desconto em duas tipografias (Inter 12/700 no símbolo, Geist Mono 13/600 no
 * valor) e embute o do preço num texto único em Geist Mono. Aqui os dois
 * campos seguem a forma do desconto — símbolo em `font-sans` fora do
 * `<input>`, valor em `font-mono` dentro — porque um `<input>` comporta uma
 * tipografia só, e o símbolo precisa ficar fora dele para não virar máscara
 * sobre o texto que o operador edita.
 */
export interface EntradaRapidaProdutoProps {
  /**
   * Superfície extra no cabeçalho da barra, ao lado da lupa — hoje só o botão
   * "Scanner" da feature 007 (nó `QIJKL` do Pencil, que o desenho põe
   * exatamente aí).
   *
   * Recebe `aoLerCodigo`, **o mesmo caminho de entrada do código digitado**: a
   * string decodificada vai para o campo e é consultada como na saída dele
   * (`revisarPorCodigo`, AD-266), é classificada por `EntradaCodigo`
   * (simples/com-quantidade/balança) e vira linha pelo mesmo
   * `carrinhoSlice.inserirItem` (`FR-007` da 007, D5). É por isso que o slot é
   * uma função e não um `ReactNode` solto: quem monta o botão não precisa —
   * nem consegue — inventar um segundo caminho de inserção.
   */
  readonly renderizarCaptura?: (
    aoLerCodigo: (codigo: string) => void,
    /**
     * Por que nenhum produto entra agora (venda congelada), ou `null` — o botão
     * de captura se fecha com a mesma frase da lupa e do campo de código
     * (pedido do usuário, 2026-10-08).
     */
    bloqueio: MotivoBloqueio,
  ) => ReactNode;
  /**
   * A barra está no layout de toque, onde o código é digitado no teclado
   * virtual (pedido do usuário, 2026-09-24): o campo de código abre o teclado
   * **numérico** — a maioria dos códigos é só número — e ganha o botão ABC/123
   * para trocar para o de letras.
   *
   * O botão não é enfeite: nem o teclado numérico do Android nem o do iPhone
   * têm tecla para voltar às letras, e sem ele um código alfanumérico — ou o
   * `*` do multiplicador de quantidade — não teria como ser digitado.
   *
   * Prop do layout, e não leitura de layout aqui dentro, pela mesma razão do
   * `renderizarCaptura`: quem sabe que a tela é a do celular é o wizard. No
   * desktop o teclado é físico e o `inputMode` não teria efeito — exceto num
   * notebook de toque, onde abriria um teclado numérico sem o botão de volta.
   */
  readonly tecladoVirtual?: boolean;
}

/**
 * Teclado virtual do campo de código: `'numeric'` por padrão, `'text'` depois
 * do ABC. A escolha vale até a barra desmontar — quem trocou para letras
 * provavelmente trabalha com códigos alfanuméricos, e voltar ao numérico a cada
 * item o obrigaria a tocar no ABC toda vez.
 */
type TecladoDoCodigo = 'numeric' | 'text';

export function EntradaRapidaProduto({
  renderizarCaptura,
  tecladoVirtual = false,
}: EntradaRapidaProdutoProps = {}): ReactElement {
  const { inserirPorCodigo, confirmarEdicao, revisarPorCodigo, confirmarPrevia } =
    useInsercaoDeProduto();
  const { confirmarEdicaoDeLinha } = useEdicaoDeItemExistente();
  // Correção do usuário (2026-09-03): lápis da grid/lista mobile carrega uma
  // linha já inserida de volta para cá via `useEdicaoItemStore` (irmãos em
  // `TelaDeVenda`, sem relação de pai/filho) — ver `linhaEmEdicao` abaixo.
  const linhaEmEdicao = useEdicaoItemStore((estado) => estado.linhaEmEdicao);
  const limparEdicao = useEdicaoItemStore((estado) => estado.limparEdicao);
  // Pelo `janelasStore` desde a feature 016: o F4 mora em `AppShell` e não
  // alcançava um `useState` desta barra. Só a janela sobe — o código escolhido
  // no modal continua sendo tratado aqui (`research.md` D8).
  const buscaAberta = useJanelasStore((estado) => estado.janela === 'produto');
  const abrirJanela = useJanelasStore((estado) => estado.abrir);
  const fecharJanela = useJanelasStore((estado) => estado.fechar);
  // Rótulo do campo depende de `SessaoUsuario.UsuarioTipoCodigoProduto`
  // (`GetSessao`) — é configuração da empresa, nunca um texto fixo (mesmo
  // valor que `Tipocodproduto` leva em toda chamada a `GetProduto`, AD-033).
  const contextoPrecificacao = useContextoPrecificacao();
  const rotuloCampoCodigo =
    contextoPrecificacao === null
      ? 'Código do produto'
      : rotuloTipoCodigoProduto(contextoPrecificacao.tipoCodProduto);

  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [resolvido, setResolvido] = useState<RevisaoProduto | null>(null);
  const [quantidadeTexto, setQuantidadeTexto] = useState(() =>
    formatarQuantidade(QUANTIDADE_INICIAL, 3),
  );
  /**
   * Último saldo de estoque conhecido do produto na barra (AD-236): vem da
   * resolução (TAB, Enter, modal, câmera) e é atualizado por cada confirmação
   * recusada. No lápis começa desconhecido — a primeira confirmação é que
   * consulta — e a partir daí o `+`/`−` passa a reavaliar com ele.
   */
  const [saldoConhecido, setSaldoConhecido] = useState<SaldoMilesimos | null>(null);

  const campoCodigo = useRef<HTMLInputElement>(null);
  const [tecladoDoCodigo, setTecladoDoCodigo] = useState<TecladoDoCodigo>('numeric');
  /**
   * O ABC/123 está tirando e devolvendo o foco do campo de código — ver
   * `alternarTecladoDoCodigo`. Enquanto dura, o `blur` não é o operador saindo
   * do campo, e revalidar o código no ERP ali consultaria um código pela metade.
   */
  const trocandoTeclado = useRef(false);
  /**
   * Entrada que **produziu** a revisão na tela — o texto cru, não o código
   * canônico do produto: `4*789` resolve o produto `789`, e comparar contra o
   * snapshot faria toda saída do campo revalidar um código que já foi revisado.
   *
   * `useRef` e não `useState` porque nada no render depende dela: ela só
   * responde "o que está no campo ainda é o que foi ao ERP?" no momento em que
   * o foco sai (`aoSairDoCodigo`) ou em que se confirma (`confirmar`).
   */
  const entradaRevisada = useRef<string | null>(null);
  /**
   * Entrada que o ERP **acabou de recusar** — cenário tributário, produto não
   * encontrado, preço zerado — e que continua no campo para o operador corrigir.
   *
   * Existe para a saída do campo não reconsultar a mesma resposta (correção do
   * usuário, 2026-10-05): no celular, sair do campo consulta (AD-254), e o toque
   * no "Entendi" da janela de cenário é uma saída. O mesmo código ia ao ERP de
   * novo e a janela reabria a cada fechamento. Enter, TAB e o "+" continuam
   * consultando: são pedidos explícitos, e o cadastro pode ter sido corrigido.
   */
  const entradaRecusada = useRef<string | null>(null);
  /**
   * O desconto manual que o operador pediu antes de a barra ajustá-lo ao preço
   * mínimo (AD-280), ou `null` sem ajuste pendente. Ref, e não estado: só a
   * confirmação lê, e o valor não muda nada na tela.
   *
   * Zera quando o pedido deixa de existir — o operador digita de novo no preço
   * ou no desconto, a barra volta ao vazio ou carrega outro item.
   */
  const descontoPedidoAntesDoMinimo = useRef<Centavos | null>(null);
  /**
   * Uma recusa abriu a janela de cenário e o foco ainda precisa voltar ao
   * código quando ela fechar — ver `voltarAoCodigoAposRecusa`.
   */
  const focarCodigoAoFecharCenario = useRef(false);
  const janelaDeCenarioAberta = useCenarioProdutoStore((estado) => estado.recusa !== null);
  const campoQuantidade = useRef<HTMLInputElement>(null);
  // Preço e desconto ganharam ref pelo mesmo motivo que a quantidade sempre
  // teve: sair deles com valor inválido devolve o foco ao campo (`exigirCampo`).
  const campoPreco = useRef<HTMLInputElement>(null);
  const campoDesconto = useRef<HTMLInputElement>(null);
  const botaoConfirmar = useRef<HTMLButtonElement>(null);
  /** Contador de TABs do desconto para o "+" — ver `aoTeclarNoCartao`. */
  const [pedidosDeInsercaoPorTab, setPedidosDeInsercaoPorTab] = useState(0);

  // `linhaEmEdicao` (item já inserido, recarregado pelo lápis) e `resolvido`
  // (produto novo em revisão, TAB/busca) nunca coexistem — cada um "vence" o
  // outro no ponto em que passa a existir (ver `aplicarRevisao`/efeito abaixo)
  // — mas os derivados leem os dois porque a UI é a mesma barra.
  const editavel = linhaEmEdicao
    ? linhaEmEdicao.snapshot.pesavelEditavel === 'E'
    : (resolvido?.editavel ?? false);
  const semResolucao = linhaEmEdicao === null && resolvido === null;
  const snapshotAtivo = linhaEmEdicao?.snapshot ?? resolvido?.snapshot ?? null;
  // Desconto de convênio da linha em edição: fixo, nunca digitado pelo
  // operador aqui — só `repricarSku` escreve (`descontoDeConvenio`,
  // `reprecificacao.ts`) — soma no total sem entrar no campo de desconto, que
  // representa exclusivamente o `descontoManual` que este formulário grava.
  const descontoConvenioFixo = linhaEmEdicao?.descontoConvenio ?? ZERO_CENTAVOS;
  const quantidadeLida = lerQuantidadeTexto(quantidadeTexto);
  /**
   * Preço e desconto da barra (017, AD-279): em produto `'E'` com preço de
   * tabela eles são a mesma informação vista de dois lados, e o hook decide
   * qual recalcular. O par que a linha guarda sai de `consolidar()` — o preço
   * na tela é derivado e não entra no total como se fosse a referência.
   */
  const vinculo = useVinculoPrecoDesconto({ snapshot: snapshotAtivo, quantidade: quantidadeLida });
  const {
    precoTexto,
    descontoTexto,
    carregar: carregarVinculo,
    iniciar: iniciarVinculo,
    zerar: zerarVinculo,
  } = vinculo;
  const precoDigitado = editavel ? lerCentavosDigitados(precoTexto) : null;
  const descontoDigitado = editavel ? lerCentavosDigitados(descontoTexto) : null;
  const consolidado = editavel && vinculo.vinculavel ? vinculo.consolidar() : null;
  const precoLido = editavel
    ? (consolidado?.precoUnitario ?? precoDigitado)
    : (linhaEmEdicao?.precoUnitario ?? resolvido?.snapshot.precoBase ?? null);
  const descontoManualLido = editavel
    ? (consolidado?.descontoManual ?? descontoDigitado)
    : (linhaEmEdicao?.descontoManual ?? ZERO_CENTAVOS);
  // Preço derivado do desconto não é digitação do operador: um desconto que
  // leva o preço a zero é o caso de `descontoZeraItem`, não de preço inválido.
  const precoDerivado = vinculo.vinculavel && vinculo.mestre === 'DESCONTO';

  /**
   * Limita o desconto ao preço mínimo do produto e avisa (017, US2). Roda ao
   * sair do preço ou do desconto e na confirmação; como o par ajustado já sai
   * daqui dentro do limite, o slice não repete o aviso (FR-013).
   */
  function ajustarAoMinimoEAvisar(): boolean {
    if (!editavel || snapshotAtivo === null) {
      return false;
    }
    // Lido **antes** do ajuste: depois dele o campo já mostra o permitido, e o
    // que o operador pediu só existe aqui.
    const pedido = vinculo.consolidar()?.descontoManual ?? null;
    if (vinculo.ajustarAoMinimo() === null) {
      return false;
    }
    if (pedido !== null) {
      descontoPedidoAntesDoMinimo.current = pedido;
    }
    notificar.aviso(avisoDePrecoMinimo(snapshotAtivo.precoMinimo));
    return true;
  }

  /**
   * O que a confirmação grava, mais o desconto pedido quando a barra o ajustou
   * ao preço mínimo — o `Log` registra os dois (AD-280).
   */
  function ajustesParaGravar(quantidade: Milesimos, par: ItemConsolidado): AjustesDaBarra {
    const pedido = descontoPedidoAntesDoMinimo.current;
    return {
      quantidade,
      ...par,
      ...(pedido === null || pedido === par.descontoManual
        ? {}
        : { descontoManualSolicitado: pedido }),
    };
  }

  /**
   * O par preço/desconto que a confirmação grava. No produto vinculável ele sai
   * do hook **depois** do ajuste ao mínimo — o estado do React ainda é o do
   * quadro anterior dentro deste mesmo gesto.
   */
  function parParaGravar(): ItemConsolidado | null {
    if (editavel && vinculo.vinculavel) {
      ajustarAoMinimoEAvisar();
      return vinculo.consolidar();
    }
    return precoLido === null || descontoManualLido === null
      ? null
      : { precoUnitario: precoLido, descontoManual: descontoManualLido };
  }
  const descontoTotalLido =
    descontoManualLido === null ? null : somar(descontoConvenioFixo, descontoManualLido);

  /**
   * Total da linha como ela entraria na venda — a mesma função do domínio que
   * o carrinho usa (`calcularTotalLinha`), nunca uma subtração local: o total
   * exibido, o total gravado e o total validado precisam ser o mesmo número.
   */
  const totalItemLido =
    quantidadeLida === null || precoLido === null || descontoTotalLido === null
      ? null
      : calcularTotalLinha(precoLido, quantidadeLida, descontoTotalLido);

  /**
   * O desconto digitado consome o item inteiro (pedido do usuário,
   * 2026-09-04). Só é possível em produto `'E'`, o único em que o campo de
   * desconto aceita digitação — nos demais o valor exibido é o da própria linha
   * e o operador não tem como estragá-lo daqui.
   *
   * `calcularTotalLinha` tem piso zero (invariante I8), então um desconto
   * exagerado não produz total negativo: ele produz **zero**, que é exatamente
   * o desfecho que esta guarda existe para recusar — um produto entregue de
   * graça, sem ninguém ser avisado.
   */
  const descontoZeraItem =
    editavel && totalItemLido !== null && totalItemLido < TOTAL_MINIMO_DA_LINHA;

  /**
   * Os três campos digitáveis da prévia são obrigatórios (pedido do usuário,
   * 2026-09-04) — ver os `AVISO_*` no topo do arquivo para o porquê de o zero
   * ser recusado em quantidade e preço, mas não em desconto.
   *
   * `precoInvalido` e `descontoInvalido` só existem quando o campo é de fato
   * digitável (`editavel`): em produto `'S'`/`'B'`/`''` os dois valores vêm do
   * cadastro ou da própria linha, o campo é `readOnly` e o operador não tem
   * como estragá-los daqui — bloqueá-lo por um preço zerado no cadastro seria
   * um beco sem saída, sem campo onde corrigir.
   */
  const quantidadeInvalida = quantidadeLida === null;
  const precoInvalido =
    editavel && !precoDerivado && (precoDigitado === null || precoDigitado <= ZERO_CENTAVOS);
  const descontoInvalido = editavel && descontoDigitado === null;

  /**
   * Saldo de estoque da quantidade na barra, reavaliado **a cada render**
   * (AD-236): o `+`/`−` e a digitação mudam o veredito na hora, com o saldo já
   * buscado. A confirmação reconsulta o ERP antes de mutar a venda.
   */
  const politicaSaldo = usePoliticaSaldo();
  const linhasDaVenda = useVendaStore((estado) => estado.linhas);
  function avaliarQuantidade(quantidade: Milesimos | null): AvaliacaoSaldo {
    if (snapshotAtivo === null || quantidade === null) {
      return AVALIACAO_LIVRE;
    }
    return avaliarContraCarrinho(politicaSaldo, linhasDaVenda, {
      snapshot: snapshotAtivo,
      saldo: saldoConhecido,
      quantidade,
      ...(linhaEmEdicao === null ? {} : { linhaEditada: linhaEmEdicao }),
    });
  }
  const avaliacaoSaldo = avaliarQuantidade(quantidadeLida);
  const motivoSaldo = avaliacaoSaldo.veredito === 'bloqueio' ? avaliacaoSaldo.frase : null;

  /**
   * Última frase de saldo anunciada para a prévia na barra (correção do
   * usuário, 2026-09-17): o aviso sai ao sair da quantidade **ou** ao inserir,
   * nunca nos dois com a mesma frase. A confirmação recebe esta frase e não
   * repete um aviso igual (`avisoJaComunicado`).
   */
  const saldoAnunciado = useRef<string | null>(null);

  function anunciarSaldo(avaliacao: AvaliacaoSaldo): void {
    if (avaliacao.veredito === 'livre' || avaliacao.frase === saldoAnunciado.current) {
      return;
    }
    saldoAnunciado.current = avaliacao.frase;
    if (avaliacao.veredito === 'bloqueio') {
      notificar.erro(avaliacao.frase);
      return;
    }
    notificar.aviso(avaliacao.frase);
  }

  /**
   * Avisa **uma vez por mudança que cruza o limite** — de livre para acima do
   * saldo —, não a cada tecla nem a cada `+` já acima dele.
   *
   * Vale para as duas políticas desde o AD-239: com a linha de aviso removida
   * da barra, o toast é o **único** canal, e em `'B'` o operador precisa saber
   * por que o botão acabou de travar — antes disso o bloqueio só se anunciava
   * ao confirmar. O botão continua carregando o motivo no `title`.
   */
  function avisarSeCruzouSaldo(novaQuantidade: Milesimos | null): void {
    const depois = avaliarQuantidade(novaQuantidade);
    if (avaliacaoSaldo.veredito !== 'livre' || depois.veredito === 'livre') {
      return;
    }
    anunciarSaldo(depois);
  }

  // Foco automático ao resolver (TAB) ou ao recarregar uma linha existente
  // (lápis): produto editável pousa na quantidade — primeiro campo da
  // sequência de revisão, nunca no botão de inserir; não editável não tem
  // nada a decidir além do que o stepper já resolve, então pousa direto no
  // "+" (Enter/clique já confirma, sem exigir mouse).
  useEffect(() => {
    if (resolvido === null) {
      return;
    }
    // Prévia bloqueada por saldo também pousa na quantidade: reduzi-la é a
    // saída que o operador tem (AD-236), e o botão bloqueado nem está na
    // ordem de TAB.
    if (resolvido.editavel || resolvido.avaliacaoSaldo.veredito === 'bloqueio') {
      campoQuantidade.current?.focus();
      campoQuantidade.current?.select();
    } else {
      // Sem teclado: no "+" não há o que digitar (pedido do usuário,
      // 2026-09-24), e o produto escolhido no modal de busca chega aqui com o
      // teclado da busca ainda aberto.
      focarSemTeclado(botaoConfirmar.current);
    }
  }, [resolvido]);

  useEffect(() => {
    if (linhaEmEdicao === null) {
      return;
    }
    // Nova revisão de inserção "vence" uma edição pendente por baixo, se
    // houver (guarda espelhada em `aplicarRevisao`).
    setResolvido(null);
    setSaldoConhecido(null);
    // O código do item em edição é o que já está na venda: nunca vai ao ERP
    // de novo por este caminho — o campo fica `disabled` (correção do usuário,
    // 2026-09-16) —, mas a marca acompanha o texto para que nenhum resto da
    // revisão anterior sobreviva à troca.
    entradaRevisada.current = linhaEmEdicao.snapshot.codigoProduto;
    setTexto(linhaEmEdicao.snapshot.codigoProduto);
    setQuantidadeTexto(formatarQuantidade(linhaEmEdicao.quantidade, 3));
    // O par **derivado** (017): o preço praticado, não o `precoUnitario` de
    // referência — que, com desconto, é o preço de tabela.
    descontoPedidoAntesDoMinimo.current = null;
    carregarVinculo(linhaEmEdicao);
    if (linhaEmEdicao.snapshot.pesavelEditavel === 'E') {
      campoQuantidade.current?.focus();
      campoQuantidade.current?.select();
    } else {
      focarSemTeclado(botaoConfirmar.current);
    }
  }, [carregarVinculo, linhaEmEdicao]);

  /**
   * Foco de volta pro código sempre que a barra volta ao estado vazio —
   * depois de confirmar (Enter em qualquer campo ou clique no "+") ou
   * cancelar (Escape). Pedido do usuário (2026-09-03): o operador precisa
   * poder bipar/digitar o próximo item sem tocar no mouse.
   *
   * Precisa ser um efeito, não uma chamada direta dentro de `resetar()`: o
   * campo de código só deixa de estar `disabled` depois que o React aplica
   * `setResolvido(null)`/`limparEdicao()` ao DOM — chamar `.focus()` na mesma
   * função síncrona que dispara esses `set` acontece **antes** desse commit,
   * então o campo ainda está desabilitado no instante da chamada e o
   * navegador ignora o foco em silêncio (achado do usuário: Enter inseria,
   * mas o foco não voltava).
   */
  //
  // **Não com janela aberta** (feature 016): este efeito também roda na
  // montagem, e no wizard mobile o F3/F4 monta a etapa 1 junto com o modal de
  // busca — sem a guarda, o campo de código tiraria o foco da busca que o
  // operador acabou de pedir.
  useEffect(() => {
    if (resolvido === null && linhaEmEdicao === null && !haJanelaAberta()) {
      campoCodigo.current?.focus();
    }
  }, [resolvido, linhaEmEdicao]);

  /**
   * Foco pedido de fora — hoje só pelo card de cliente, ao concluir uma
   * identificação (pedido do usuário, 2026-09-03).
   *
   * Efeito separado do de cima, e não uma dependência a mais nele: aquele
   * reage ao **estado da própria barra** voltar ao vazio, e reexecutá-lo por
   * um pedido externo roubaria o foco no meio de uma revisão de produto em
   * andamento. Aqui o pedido é explícito, e o guard de `resolvido` preserva a
   * mesma regra — quem está revisando um item não perde o campo.
   */
  const pedidosDeFocoNoCodigo = useFocoVendaStore((estado) => estado.pedidosDeFocoNoCodigo);
  // Sentido inverso: Shift+TAB no campo de código devolve o foco ao card de
  // cliente (ver `aoTeclarNoCodigo`).
  const focarDocumentoCliente = useFocoVendaStore((estado) => estado.focarDocumentoCliente);
  useEffect(() => {
    if (pedidosDeFocoNoCodigo > 0 && resolvido === null) {
      campoCodigo.current?.focus();
    }
    // `resolvido` fora das dependências de propósito: o efeito reage ao
    // pedido, não à barra — incluí-lo faria toda revisão concluída disparar um
    // foco extra em nome de um pedido antigo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidosDeFocoNoCodigo]);

  /**
   * Foco de volta ao código quando a janela de cenário fecha (correção do
   * usuário, 2026-10-05, AD-266), com o texto selecionado: o código recusado
   * fica no campo e a próxima tecla o substitui.
   *
   * Efeito desta barra, e não o retorno de foco da própria janela: o
   * `useFocoDeModal` devolve o foco a quem estava focado quando ela abriu — no
   * celular, a quantidade em que o operador tocou e cuja saída do código fez a
   * consulta. Este efeito roda depois daquela devolução (limpezas de efeito
   * passivo vêm antes das montagens no mesmo commit), então é o que prevalece.
   */
  useEffect(() => {
    if (janelaDeCenarioAberta || !focarCodigoAoFecharCenario.current) {
      return;
    }
    focarCodigoAoFecharCenario.current = false;
    campoCodigo.current?.focus();
    campoCodigo.current?.select();
  }, [janelaDeCenarioAberta]);

  /**
   * Código recusado pelo ERP: o foco volta ao campo para a redigitação.
   *
   * **Com a janela de cenário aberta, só depois de ela fechar** (AD-266). Focar
   * agora deixaria o teclado virtual aberto por baixo da janela, e é ela que o
   * operador precisa ler. Então o teclado fecha — o campo focado perde o foco —,
   * e o efeito acima traz o foco de volta no "Entendi".
   *
   * A janela já está no store quando a recusa chega aqui: quem a abre é
   * `revisarPorCodigo`/`inserirPorCodigo`, antes de devolver `'recusado'`.
   * Recusa em toast (produto não encontrado, sem preço) não prende nada e foca
   * na hora, como sempre.
   */
  function voltarAoCodigoAposRecusa(): void {
    if (useCenarioProdutoStore.getState().recusa === null) {
      campoCodigo.current?.focus();
      return;
    }
    focarCodigoAoFecharCenario.current = true;
    fecharTecladoVirtual();
  }

  /**
   * A venda está congelada (condição escolhida ou pagamento aprovado) e nenhum
   * produto entra mais (pedido do usuário, 2026-10-08).
   *
   * A recusa já existia no fim do gesto — `inserirItem` devolve o aviso —, mas
   * o campo de código e a lupa seguiam abertos e convidavam o operador a
   * bipar uma compra que a venda ia descartar. A frase é a mesma do lápis e da
   * lixeira da grid (`useMotivoCarrinhoBloqueado`), com a saída "Limpar".
   *
   * `readOnly` + `aria-disabled`, e não `disabled` (AD-143): o campo precisa
   * receber o clique para explicar o motivo, e o `disabled` nativo o engole.
   * Com o lápis o código já fica `disabled` por outra razão (`linhaEmEdicao`),
   * então este bloqueio só vale fora dele.
   */
  const bloqueioDoCarrinho = useMotivoCarrinhoBloqueado();
  const bloqueioDoCodigo: MotivoBloqueio = linhaEmEdicao === null ? bloqueioDoCarrinho : null;

  // Terceiro sentido do `focoVendaStore`: daqui para o campo do vendedor,
  // quando a venda ainda não tem um (ver `exigirVendedor`).
  const semVendedor = useVendedorAtual() === null;
  const focarVendedor = useFocoVendaStore((estado) => estado.focarVendedor);

  /**
   * Porta única da regra "sem vendedor não se insere produto" (pedido do
   * usuário, 2026-09-10): responde `false` depois de avisar e de levar o foco
   * ao campo do vendedor.
   *
   * É chamada em **cada gesto** que pode terminar num item no grid —
   * `capturarPorCamera`, `confirmarEntradaRapida`, `resolverEExibir` e
   * `confirmar` — e não num ponto único mais abaixo, porque os quatro entram
   * por caminhos diferentes: câmera, leitor/Enter no código, TAB/modal de busca
   * e confirmação da prévia. Barrar só no último deixaria a consulta ao ERP
   * acontecer para um produto que nunca vai entrar.
   *
   * **Editar linha já existente não passa por aqui.** O lápis mexe em item que
   * já está na venda — inclusive numa venda vinda de DAV/rascunho sem vendedor
   * (`trocarVendedor` com `codigo <= 0`, AD-095) — e travar a correção de
   * quantidade de um item já lançado não impede NFCe nenhuma: quem impede é o
   * "Finalizar" da 004, que continua exigindo o vendedor.
   */
  function exigirVendedor(): boolean {
    if (!semVendedor) {
      return true;
    }
    notificar.erro(AVISO_SEM_VENDEDOR);
    focarVendedor();
    return false;
  }

  /**
   * Venda congelada não recebe produto: responde `false` depois de dizer o
   * motivo. Vem **antes** de `exigirVendedor` e de qualquer consulta ao ERP —
   * pedir um `GetProduto` para um item que a venda vai recusar é só latência —,
   * nos mesmos pontos de entrada que ela guarda: Enter, TAB/modal, câmera e a
   * confirmação da prévia.
   */
  function exigirCarrinhoLivre(): boolean {
    if (bloqueioDoCarrinho === null) {
      return true;
    }
    notificar.erro(bloqueioDoCarrinho);
    return false;
  }

  function resetar(): void {
    setResolvido(null);
    entradaRevisada.current = null;
    entradaRecusada.current = null;
    saldoAnunciado.current = null;
    setTexto('');
    setQuantidadeTexto(formatarQuantidade(QUANTIDADE_INICIAL, 3));
    descontoPedidoAntesDoMinimo.current = null;
    zerarVinculo();
    setSaldoConhecido(null);
    limparEdicao();
  }

  function alterarQuantidade(delta: number): void {
    const atual = quantidadeLida ?? QUANTIDADE_INICIAL;
    const proxima = delta > 0 ? somarQuantidades(atual, UMA_UNIDADE) : atual - UMA_UNIDADE;
    const nova = milesimos(Math.max(UMA_UNIDADE, proxima));
    avisarSeCruzouSaldo(nova);
    setQuantidadeTexto(formatarQuantidade(nova, 3));
    vinculo.mudarQuantidade(nova);
  }

  /**
   * Roda uma confirmação que consulta o saldo (AD-236): recusada, a barra
   * fica como está — com o saldo novo, para o botão refletir o bloqueio —;
   * aceita, volta ao estado vazio.
   */
  async function aplicarConfirmacao(acao: () => Promise<ResultadoConfirmacao>): Promise<void> {
    setOcupado(true);
    try {
      const resultado = await acao();
      if (resultado.situacao === 'bloqueado') {
        setSaldoConhecido(resultado.saldo);
        return;
      }
      // O slice recusou a edição e já disse o porquê (017): o que o operador
      // digitou fica na barra para ele corrigir.
      if (resultado.situacao === 'recusado') {
        return;
      }
      resetar();
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Inserção rápida do Enter (e do leitor físico, que termina a bipagem com
   * Enter). A câmera não passa mais por aqui desde AD-266: ela segue o caminho
   * da saída do campo (`capturarPorCamera`).
   */
  async function confirmarEntradaRapida(): Promise<void> {
    const entrada = texto.trim();
    if (entrada === '' || ocupado) {
      return;
    }

    if (!exigirCarrinhoLivre() || !exigirVendedor()) {
      return;
    }

    /**
     * A revisão em curso — prévia resolvida ou item carregado pelo lápis —
     * **perde** para o código que acabou de ser digitado ou bipado (correção do
     * usuário, 2026-09-16). Era o contrário até então: a digitação parava aqui,
     * porque o campo de código ficava desabilitado e ninguém voltava a ele; com
     * o campo sempre acessível, insistir na guarda faria o Enter não responder.
     *
     * É a mesma política de `capturarPorCamera` e `selecionarDaBusca`: apontar
     * a câmera para outra etiqueta, escolher outro produto no modal e digitar
     * outro código são gestos igualmente deliberados.
     *
     * Só a revisão é descartada — o texto digitado fica, porque é justamente o
     * que vai ser resolvido agora.
     */
    setResolvido(null);
    setSaldoConhecido(null);
    limparEdicao();

    setOcupado(true);
    try {
      const resultado = await inserirPorCodigo(entrada);

      if (resultado.situacao === 'edicao') {
        // Produto `'E'`: a linha não entra ainda; vira revisão editável,
        // mesmo caminho de quando o TAB resolve um produto `'E'` (`FR-014`).
        // Uma edição de linha existente pendente perde para esta revisão
        // nova (mesma guarda de `aplicarRevisao`).
        aplicarRevisao(
          {
            situacao: 'revisao',
            snapshot: resultado.snapshot,
            quantidade: resultado.quantidade,
            origem: 'MANUAL',
            editavel: true,
            saldo: resultado.saldo,
            // Só decide o foco: o saldo de um `'E'` é anunciado ao sair da
            // quantidade ou ao inserir (correção do usuário, 2026-09-17).
            avaliacaoSaldo: AVALIACAO_LIVRE,
          },
          entrada,
        );
        // O código digitado permanece visível no campo (só desabilitado)
        // enquanto o operador revisa — é o que o Pencil mostra (`data-icon-name`
        // "Código digitado" convive com o resto da linha já resolvida).
        return;
      }

      if (resultado.situacao === 'bloqueado') {
        // Saldo em `'B'` (AD-236): a inserção automática — Enter ou
        // leitor — não aconteceu, e o produto fica na barra como prévia
        // bloqueada, com o código visível, para o operador reduzir a
        // quantidade ou cancelar com Escape.
        aplicarRevisao(resultado.revisao, entrada);
        // `inserirPorCodigo` já anunciou a recusa: sair da quantidade sem
        // mexer nela não repete o mesmo toast.
        if (resultado.revisao.avaliacaoSaldo.veredito !== 'livre') {
          saldoAnunciado.current = resultado.revisao.avaliacaoSaldo.frase;
        }
        return;
      }

      if (resultado.situacao === 'inserido') {
        setTexto('');
        campoCodigo.current?.focus();
        return;
      }
      // Em recusa o texto permanece: o operador corrige o que digitou.
      entradaRecusada.current = entrada;
      voltarAoCodigoAposRecusa();
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Código lido pela câmera (007) — o **único** caminho de entrada que não
   * passa pelo campo de código.
   *
   * **A leitura nova vence a prévia ou a edição pendente.** É a mesma política
   * de `selecionarDaBusca`, o irmão desta função: quem escolhe outro produto no
   * modal também descarta a revisão em curso, porque apontar a câmera para
   * outra etiqueta é um gesto tão deliberado quanto escolher na lista. Antes
   * desta correção (revisão da 007, 2026-09-09) o botão "Scanner" ficava ativo
   * enquanto o campo de código estava desabilitado, e a leitura tinha dois
   * desfechos errados: com prévia resolvida ela era descartada em silêncio,
   * deixando no campo um código que não correspondia ao produto exibido; com
   * item carregado pelo lápis, um produto novo entrava no carrinho enquanto a
   * barra seguia pulsando em modo de edição de outra linha.
   *
   * **Recusar não era opção** — seria a saída natural pelo padrão de
   * `lib/bloqueio.ts`, mas a única forma de cancelar uma prévia é `Escape`
   * (`aoTeclarNoCartao`), e no layout compacto, que é o único onde a câmera
   * existe, não há tecla nenhuma: o operador ficaria preso na prévia sem poder
   * ler nem cancelar.
   *
   * `resetar()` antes do `setTexto`, e não depois: ele limpa o campo junto com
   * o resto, então a ordem inversa apagaria o código recém-lido.
   *
   * **A leitura é o código digitado mais a saída do campo, não um Enter**
   * (correção do usuário, 2026-10-05, AD-266). Até aqui ela ia pela inserção
   * rápida (`confirmarEntradaRapida`); agora escreve o código no campo e segue
   * o caminho que a mudança de foco segue (`aoSairDoCodigo` → `resolverEExibir`,
   * o mesmo núcleo do TAB). É a saída do campo que consulta o ERP, e é ela que
   * decide, pela regra de sempre, entre inserir direto, abrir a prévia e
   * recusar — a câmera não tem regra própria.
   *
   * Chama `resolverEExibir` com o código lido, e não `revisarEntrada`, pelo
   * mesmo motivo do parâmetro de `confirmarEntradaRapida`: `texto` só valeria
   * no próximo render.
   */
  async function capturarPorCamera(codigo: string): Promise<void> {
    if (ocupado) {
      return;
    }
    // **Antes** do `resetar()` abaixo: recusada a inserção por falta de
    // vendedor, descartar a prévia em curso cobraria do operador um trabalho
    // que a leitura recusada nem chegou a substituir. O mesmo vale para a venda
    // congelada.
    if (!exigirCarrinhoLivre() || !exigirVendedor()) {
      return;
    }
    if (resolvido !== null || linhaEmEdicao !== null) {
      resetar();
    }
    setTexto(codigo);
    await resolverEExibir(codigo);
  }

  /**
   * Aplica uma revisão resolvida (`GetProduto`) ao estado local — usada tanto
   * por `resolverEExibir` (TAB) quanto por `selecionarDaBusca` (produto
   * editável/pesável escolhido no modal). Uma edição de linha existente
   * pendente perde para esta revisão nova (mesma política de
   * `capturarPorCamera`): o operador está deliberadamente resolvendo
   * outro produto.
   */
  function aplicarRevisao(revisao: RevisaoProduto, entrada: string): void {
    limparEdicao();
    // A entrada que foi ao ERP acompanha a revisão: é ela que `aoSairDoCodigo`
    // compara com o campo para saber se o código mudou desde a consulta.
    entradaRevisada.current = entrada.trim();
    saldoAnunciado.current = null;
    setResolvido(revisao);
    setSaldoConhecido(revisao.saldo);
    setQuantidadeTexto(formatarQuantidade(revisao.quantidade, 3));
    descontoPedidoAntesDoMinimo.current = null;
    iniciarVinculo(revisao.snapshot);
  }

  /**
   * Núcleo compartilhado por TAB (`revisarEntrada`, usa `texto` digitado) e
   * pela seleção no modal de busca (`selecionarDaBusca`, usa o código
   * escolhido direto) — os dois resolvem por `GetProduto` via
   * `revisarPorCodigo` e decidem entre inserir direto e mostrar a revisão pelo
   * mesmo critério.
   *
   * **Quem abre a prévia é a origem, não só o tipo do produto** (pedido do
   * usuário, 2026-09-11):
   *
   * - `''` entra direto pelos dois caminhos — não há preço nem desconto a
   *   ajustar nem etiqueta a interpretar, e a prévia só custaria uma
   *   confirmação a mais no ritmo do caixa (pedido de 2026-09-03, estendendo ao
   *   TAB o que AD-124 já fazia no modal).
   * - `'S'`/`'B'` entram direto **pelo TAB** e abrem a prévia **pelo modal**. O
   *   TAB é continuação de um código que o operador já tem em mãos — a etiqueta
   *   bipada ou digitada —, então parar ali repete o gesto que o Enter resolve
   *   numa tecla. Escolher no modal é outra coisa: o operador chegou por
   *   descrição, não pelo código, e a prévia é onde ele confere que o pesável
   *   escolhido é mesmo o que queria antes de somar peso ao carrinho.
   * - `'E'` nunca entra direto: sem o preço digitado não há linha (`FR-014`).
   */
  async function resolverEExibir(
    codigo: string,
    opcoes?: { origem?: 'BUSCA'; tipoCodigo?: string },
  ): Promise<void> {
    // O TAB e o modal de busca também terminam em item no grid quando o produto
    // é `''` (inserção direta, AD-124), então a exigência vale aqui e não só na
    // confirmação da prévia — e vale **antes** do `GetProduto`.
    if (!exigirCarrinhoLivre() || !exigirVendedor()) {
      return;
    }

    setOcupado(true);
    try {
      const resultado = await revisarPorCodigo(codigo, opcoes);
      if (resultado.situacao === 'recusado') {
        entradaRecusada.current = codigo.trim();
        voltarAoCodigoAposRecusa();
        return;
      }
      const veioDoModal = opcoes?.origem === 'BUSCA';
      const tipo = resultado.snapshot.pesavelEditavel;
      // Saldo bloqueando em `'B'` (AD-236) desvia a inserção direta para a
      // prévia bloqueada, como no Enter.
      const entraDireto = tipo === '' || (!veioDoModal && tipo !== 'E');
      const bloqueado = resultado.avaliacaoSaldo.veredito === 'bloqueio';
      if (entraDireto && !bloqueado) {
        // O saldo acabou de ser consultado; a confirmação o anuncia.
        const confirmacao = await confirmarPrevia(resultado, resultado.quantidade, {
          saldoRecemConsultado: true,
        });
        if (confirmacao.situacao === 'confirmado') {
          resetar();
          return;
        }
      }
      // Mesma razão do caminho rápido: o código digitado fica visível durante
      // a revisão, só `resetar()` (confirmar/cancelar) o limpa.
      aplicarRevisao(resultado, codigo);
      // A inserção direta que o saldo barrou se explica agora. Uma prévia que o
      // operador ainda vai revisar, não: o saldo sai ao deixar a quantidade ou
      // ao inserir (correção do usuário, 2026-09-17).
      if (entraDireto && bloqueado) {
        anunciarSaldo(resultado.avaliacaoSaldo);
      }
    } finally {
      setOcupado(false);
    }
  }

  async function revisarEntrada(): Promise<void> {
    const entrada = texto.trim();
    // `resolvido !== null` saiu da guarda (correção do usuário, 2026-09-16): com
    // o campo de código sempre acessível, o TAB dali resolve o texto novo, como
    // o Enter. Quem aplica a revisão já descarta a anterior (`aplicarRevisao`).
    if (entrada === '' || ocupado) {
      return;
    }
    await resolverEExibir(entrada);
  }

  /**
   * O que está no campo precisa ir ao ERP ao sair dele?
   *
   * **Com uma prévia de inserção** (`resolvido`): só se o código mudou desde a
   * consulta — ver `aoSairDoCodigo`. No item carregado pelo lápis o campo é
   * `disabled`, então nunca diverge.
   *
   * **Sem prévia, só no celular** (correção do usuário, 2026-09-24, AD-254):
   * digitar o código e sair do campo — pelo "OK"/"Ir" do teclado ou tocando
   * fora — não carregava nada. No desktop quem consulta é o TAB, tecla que o
   * teclado virtual não tem; sem ele, a saída do campo é o único gesto que diz
   * "terminei de digitar". No desktop a saída sem prévia continua sendo só
   * navegação — o TAB com o campo vazio segue indo para a lupa (pedido do
   * usuário, 2026-09-04), e um clique fora não é pedido de consulta.
   *
   * **Nunca o código que o ERP acabou de recusar** (correção do usuário,
   * 2026-10-05) — ver `entradaRecusada`.
   */
  function codigoPendenteDeConsulta(): boolean {
    if (entradaRecusada.current !== null && texto.trim() === entradaRecusada.current) {
      return false;
    }
    if (resolvido !== null) {
      return texto.trim() !== entradaRevisada.current;
    }
    return tecladoVirtual && linhaEmEdicao === null && texto.trim() !== '';
  }

  /**
   * Sair do campo com o código mexido revalida no ERP (correção do usuário,
   * 2026-09-16).
   *
   * Antes disto, apagar ou trocar o código e sair pelo TAB deixava na barra o
   * preço, o desconto e o total do produto **anterior** — dados que já não
   * pertenciam a nada do que estava escrito. O operador via um item coerente e
   * confirmava outro.
   *
   * Os dois desfechos ruins prendem o foco aqui, porque não há para onde
   * seguir com a revisão inválida:
   *
   * - **Código apagado:** a prévia inteira é descartada (`resetar`) — unidade,
   *   preço, desconto e total saem junto com o código que os justificava
   *   (correção do usuário, 2026-09-16). É local, sem gastar chamada.
   * - **Código inexistente:** só se descobre no ERP. `revisarPorCodigo` já
   *   avisa ("Produto X não encontrado.") e `resolverEExibir` devolve o foco ao
   *   campo, com o texto digitado preservado para o operador corrigir.
   *
   * Código igual ao que já foi revisado não consulta nada: senão, sair do campo
   * para ajustar a quantidade custaria um `GetProduto` a cada ida e volta.
   *
   * No celular, sair com um código digitado e **nenhuma** prévia também
   * consulta (`codigoPendenteDeConsulta`, AD-254) — pelo mesmo caminho do TAB.
   */
  async function aoSairDoCodigo(): Promise<void> {
    // Venda congelada: o campo está fechado e o clique nele já disse o motivo —
    // sair dele não consulta nada nem repete o aviso.
    //
    // Tela inerte (suspensão esperando o ERP, AD-280): o foco saiu porque o
    // navegador o tirou, não porque o operador terminou de digitar. Consultar
    // aqui mandaria ao ERP um `GetProduto` no meio da suspensão da venda.
    if (
      bloqueioDoCarrinho !== null ||
      (campoCodigo.current?.closest('[inert]') ?? null) !== null ||
      trocandoTeclado.current ||
      ocupado ||
      !codigoPendenteDeConsulta()
    ) {
      return;
    }
    if (texto.trim() === '') {
      descartarPrevia();
      return;
    }
    await revisarEntrada();
  }

  /**
   * Troca o teclado virtual do campo de código entre numérico e letras
   * (pedido do usuário, 2026-09-24).
   *
   * **O foco precisa sair e voltar para o teclado trocar.** O Safari só lê o
   * `inputmode` quando um campo ganha foco, e mudar o atributo com o teclado
   * aberto não muda nada na tela. Até 2026-10-05 a saída era um `blur` +
   * `focus` do próprio campo dentro do toque — o que bastava no Android, mas não
   * no iPhone (correção do usuário nessa data): lá o foco precisa passar por
   * **outro** campo, e quem faz isso é `reabrirTecladoNoModoAtual`. `flushSync`
   * porque o atributo precisa estar no DOM **antes** de o foco sair.
   *
   * Com o campo já focado o cursor volta para onde estava: o operador está no
   * meio do código, e a seleção automática de `selecionarConteudoAoFocar` faria
   * a próxima tecla apagar o que ele digitou. Com o campo fora de foco é uma
   * chegada comum, com seleção.
   */
  function alternarTecladoDoCodigo(): void {
    flushSync(() => {
      setTecladoDoCodigo((atual) => (atual === 'numeric' ? 'text' : 'numeric'));
    });
    const campo = campoCodigo.current;
    if (campo === null || campo.disabled) {
      return;
    }
    if (document.activeElement !== campo) {
      campo.focus();
      return;
    }
    const inicio = campo.selectionStart;
    const fim = campo.selectionEnd;
    // Até o foco voltar: o `blur` do campo, quando a ponte o recebe, não é o
    // operador saindo dele.
    trocandoTeclado.current = true;
    preservarSelecaoNoProximoFoco(campo);
    reabrirTecladoNoModoAtual(campo, () => {
      trocandoTeclado.current = false;
      if (inicio !== null && fim !== null) {
        campo.setSelectionRange(inicio, fim);
      }
    });
  }

  /**
   * Prévia inteira fora, foco de volta no código (correção do usuário,
   * 2026-09-16). `resetar()` é o mesmo caminho do Escape e da confirmação: um
   * só lugar decide o que "barra vazia" significa.
   */
  function descartarPrevia(): void {
    resetar();
    exigirCampo(campoCodigo, AVISO_CODIGO_OBRIGATORIO);
  }

  /**
   * Candidato escolhido no modal de busca (`CART-01`) — o modal só devolve a
   * consulta (código + `Tipocodproduto` que o casa); carregar no campo,
   * resolver via `GetProduto` e decidir entre inserir direto e mostrar a
   * revisão é responsabilidade desta barra, não do modal.
   *
   * O `tipoCodigo` da consulta acompanha a chamada em vez de valer o da sessão
   * (AD-205): o campo escolhido pode não ser o que a empresa configura — é o
   * caso do produto sem código de barras num tenant em `'B'`, que antes a
   * busca listava e nenhum caminho conseguia inserir.
   *
   * A decisão em si mora em `resolverEExibir`, o mesmo núcleo do TAB: desde a
   * correção do usuário de 2026-09-03 os dois caminhos seguem o critério
   * idêntico, e duplicá-lo aqui deixaria a barra com duas regras que podem
   * divergir.
   */
  async function selecionarDaBusca(consulta: ConsultaDeProduto): Promise<void> {
    if (ocupado) {
      return;
    }
    setTexto(consulta.codigo);
    await resolverEExibir(consulta.codigo, { origem: 'BUSCA', tipoCodigo: consulta.tipoCodigo });
  }

  /**
   * Confere os campos obrigatórios da prévia na ordem em que o operador os
   * percorre e para no primeiro inválido, avisando e devolvendo o foco a ele.
   *
   * Existe para o Enter, que confirma direto de qualquer campo e não passa por
   * `acaoBloqueavel`: sem esta guarda, Enter com um campo vazio simplesmente
   * não fazia nada — `confirmar()` retornava em silêncio e o operador ficava
   * sem inserção e sem explicação. Pelo botão "+", `bloqueioDeInsercao` já
   * barra antes e diz o mesmo motivo.
   */
  function previaValida(): boolean {
    if (quantidadeInvalida) {
      exigirCampo(campoQuantidade, AVISO_QUANTIDADE_INVALIDA);
      return false;
    }
    if (precoInvalido) {
      exigirCampo(campoPreco, AVISO_PRECO_INVALIDO);
      return false;
    }
    if (descontoInvalido) {
      exigirCampo(campoDesconto, AVISO_DESCONTO_INVALIDO);
      return false;
    }
    return true;
  }

  function confirmar(): void {
    // Uma confirmação já está consultando o saldo no ERP (AD-236): o segundo
    // Enter/clique não pode inserir por cima dela.
    if (ocupado && !semResolucao) {
      return;
    }

    // Código apagado com a prévia na tela: confirmar inseriria o produto
    // anterior, que o operador acabou de tirar do campo (correção do usuário,
    // 2026-09-16). O clique no "+" já chega depois do `blur` que descartou a
    // prévia — esta guarda é a que cobre o Enter dado de outro campo da barra,
    // que não passa por `blur` nenhum.
    if (resolvido !== null && texto.trim() === '') {
      descartarPrevia();
      return;
    }

    // **Com o foco no campo de código, quem manda é o código** (correção do
    // usuário, 2026-09-16): o operador voltou ao campo para trocar o que
    // digitou, e o Enter dali resolve o texto novo em vez de confirmar a
    // revisão que está na tela. Confirmar a revisão continua sendo o Enter de
    // qualquer outro campo da barra, o `+` e o clique no botão.
    if (
      texto.trim() !== '' &&
      campoCodigo.current !== null &&
      document.activeElement === campoCodigo.current
    ) {
      void confirmarEntradaRapida();
      return;
    }

    if (!semResolucao && !previaValida()) {
      return;
    }

    // Correção do usuário (2026-09-03): lápis da grid/lista mobile — edita a
    // linha já inserida em vez de criar uma nova (`editarItem` por campo, via
    // `confirmarEdicaoDeLinha`). Produto pesável (`'S'`/`'B'`) só libera a
    // quantidade: `precoLido`/`descontoManualLido` chegam iguais aos da
    // própria linha (campos somente leitura), então `editarItem` não muda
    // nada neles (é idempotente, `carrinhoSlice.ts`).
    if (linhaEmEdicao !== null) {
      const par = parParaGravar();
      if (quantidadeLida === null || par === null) {
        return;
      }
      // Saldo (AD-236): a linha só muda se a reconsulta deixar; recusada, o
      // item continua carregado aqui, com o botão já bloqueado pelo saldo novo.
      const linha = linhaEmEdicao;
      const ajustes = ajustesParaGravar(quantidadeLida, par);
      void aplicarConfirmacao(() => confirmarEdicaoDeLinha(linha, ajustes, saldoConhecido));
      return;
    }

    // Bloqueio por saldo **não** interrompe a confirmação (AD-239): as duas
    // confirmações abaixo reconsultam o ERP antes de decidir, e é essa
    // reconsulta que o operador precisa quando o estoque acabou de ser
    // reposto. Parar aqui com o saldo antigo o obrigaria a bipar de novo só
    // para o Checkout ir perguntar. Recusada a reconsulta, o motivo volta em
    // toast por `comunicarSaldo` e a barra fica como está.

    // Daqui para baixo é **inserção**, não edição de linha existente — e toda
    // inserção exige vendedor. `confirmarEntradaRapida` repete a checagem por
    // ser também o alvo da câmera; aqui ela cobre a confirmação da prévia, que
    // chama `confirmarEdicao`/`confirmarPrevia` direto. A venda congelada
    // entra na mesma porta.
    if (!exigirCarrinhoLivre() || !exigirVendedor()) {
      return;
    }

    if (resolvido === null) {
      void confirmarEntradaRapida();
      return;
    }
    if (quantidadeLida === null) {
      return;
    }
    // As duas confirmações reconsultam o saldo antes de inserir (AD-236).
    const revisao = resolvido;
    const quantidade = quantidadeLida;
    const opcoesSaldo = { avisoJaComunicado: saldoAnunciado.current };
    if (revisao.editavel) {
      const par = parParaGravar();
      if (par === null) {
        return;
      }
      const ajustes = ajustesParaGravar(quantidade, par);
      void aplicarConfirmacao(() =>
        confirmarEdicao(
          {
            situacao: 'edicao',
            snapshot: revisao.snapshot,
            quantidade,
            saldo: saldoConhecido,
          },
          ajustes,
          opcoesSaldo,
        ),
      );
    } else {
      void aplicarConfirmacao(() =>
        confirmarPrevia({ ...revisao, saldo: saldoConhecido }, quantidade, opcoesSaldo),
      );
    }
  }

  // Enter só é tratado aqui pra TAB — a inserção/confirmação por Enter é
  // única e vive em `aoTeclarNoCartao` (pedido do usuário, 2026-09-03: Enter
  // confirma a partir de qualquer campo da barra, não só do código).
  function aoTeclarNoCodigo(evento: KeyboardEvent<HTMLInputElement>): void {
    if (evento.key !== 'Tab') {
      return;
    }
    // Shift+TAB volta para o passo anterior do fluxo do caixa: a identificação
    // do cliente (pedido do usuário, 2026-09-04). A ordem natural do DOM
    // levaria ao botão "Recolhido" do cabeçalho do card — um controle de
    // layout, não uma etapa da venda —, e o campo de documento nem é
    // alcançável enquanto o card está recolhido (`inert`). Por isso o pedido
    // vai pelo `focoVendaStore`: quem expande e foca é o `CampoClienteVenda`,
    // dono desse estado.
    if (evento.shiftKey) {
      evento.preventDefault();
      focarDocumentoCliente();
      return;
    }
    // Campo vazio: não há código a revisar, então TAB volta a ser a tecla de
    // navegação e segue para a próxima ação da barra — a lupa de busca, que é
    // o próximo elemento focável no DOM (pedido do usuário, 2026-09-04).
    // Sem isto, o `preventDefault()` abaixo engolia o TAB e o foco ficava
    // preso no campo vazio, obrigando o operador a pegar o mouse para abrir a
    // busca por termo livre.
    if (texto.trim() === '') {
      return;
    }
    // Com código digitado, TAB não sai do campo: no PDV ele é a tecla de
    // revisão, não de navegação (AD-027/AD-063).
    evento.preventDefault();
    void revisarEntrada();
  }

  /**
   * Enter em **qualquer** campo de texto da barra confirma — insere um
   * produto novo ou aplica a edição de um item existente, conforme o estado
   * (`confirmar()` já decide isso). Pedido direto do usuário (2026-09-03):
   * antes só o campo de código reagia a Enter; quantidade/preço/desconto
   * exigiam clicar no "+" com o mouse.
   *
   * Escuta no `<div>` do cartão (não em cada `<input>`) porque o evento sobe
   * por bubbling — um único handler cobre os campos existentes e qualquer um
   * que vier a ser adicionado, sem precisar fiar `onKeyDown` em cada um. O
   * filtro por `HTMLInputElement` exclui os botões (buscar, +/-, confirmar):
   * Enter num botão focado já dispara o `click` nativo dele, e sem o filtro
   * este handler chamaria `confirmar()` de novo por cima, duplicando o efeito.
   */
  function aoTeclarNoCartao(evento: KeyboardEvent<HTMLDivElement>): void {
    if (evento.key === 'Escape' && (resolvido !== null || linhaEmEdicao !== null)) {
      resetar();
      return;
    }
    // **TAB no desconto insere** (pedido do usuário, 2026-10-16): no produto
    // editável o desconto é o último campo antes do "+", e o TAB que chegava ao
    // botão só o focava — o operador que percorria a barra pelo teclado
    // precisava de um Enter a mais em cada item. O foco vai ao "+" pelo código
    // (inclusive quando ele está bloqueado e fora da ordem de TAB, para o
    // motivo ser dito), a saída do desconto roda como sempre, e quem insere é
    // o efeito de `pedidosDeInsercaoPorTab`, depois desse render.
    if (
      evento.key === 'Tab' &&
      !evento.shiftKey &&
      !evento.altKey &&
      !evento.ctrlKey &&
      !evento.metaKey &&
      editavel &&
      evento.target === campoDesconto.current
    ) {
      evento.preventDefault();
      botaoConfirmar.current?.focus();
      setPedidosDeInsercaoPorTab((pedidos) => pedidos + 1);
      return;
    }
    if (evento.key === 'Enter' && evento.target instanceof HTMLInputElement) {
      evento.preventDefault();
      confirmar();
    }
  }

  const podeConfirmar = semResolucao
    ? !ocupado && texto.trim() !== ''
    : !quantidadeInvalida &&
      !precoInvalido &&
      !descontoInvalido &&
      precoLido !== null &&
      descontoTotalLido !== null &&
      !descontoZeraItem &&
      motivoSaldo === null;

  /**
   * Por que o botão de inserir está bloqueado — a frase que o operador lê ao
   * clicar nele bloqueado (padrão de `lib/bloqueio.ts`, pedido do usuário
   * 2026-09-03), ou `null` quando dá para inserir.
   *
   * Os motivos são exatamente os termos de `podeConfirmar`, na mesma ordem:
   * sem esse espelho, o texto poderia dizer uma coisa e o bloqueio responder a
   * outra. Cada campo obrigatório vazio ou zerado responde com o mesmo aviso
   * que `previaValida` dá ao sair dele (pedido do usuário, 2026-09-04) — o
   * operador lê a mesma frase pelos dois caminhos, em vez de um genérico aqui
   * e um específico ali. O desconto que zera o item vem depois dos três porque
   * ele **é** um valor bem formado: o operador digitou um número legítimo que
   * a regra de negócio recusa, e só faz sentido apontá-lo quando não há mais
   * nenhum campo por preencher.
   */
  /**
   * O "+" está bloqueado por falta de vendedor.
   *
   * Booleano à parte, e não só um termo do encadeado abaixo, porque o clique
   * precisa distinguir **este** motivo dos demais: só ele responde por
   * `exigirVendedor`, que também move o foco.
   *
   * `linhaEmEdicao === null` porque com o lápis o mesmo botão confirma a
   * **edição** de um item já lançado, que a regra não alcança (ver
   * `exigirVendedor`) — bloqueá-lo ali prenderia o operador numa venda retomada
   * sem vendedor, sem poder nem corrigir a quantidade.
   */
  const bloqueadoPorVendedor = semVendedor && linhaEmEdicao === null;

  const bloqueioDeInsercao: MotivoBloqueio = bloqueadoPorVendedor
    ? // Primeiro de todos: sem vendedor nenhum outro motivo importa, e a barra
      // pode estar perfeitamente preenchida — `podeConfirmar` diria `true` e o
      // botão apareceria livre para uma inserção que `exigirVendedor` recusa.
      AVISO_SEM_VENDEDOR
    : podeConfirmar
      ? null
      : ocupado
        ? 'Aguarde: o produto ainda está sendo consultado no ERP.'
        : semResolucao
          ? 'Digite ou bipe o código do produto para inserir.'
          : quantidadeInvalida
            ? AVISO_QUANTIDADE_INVALIDA
            : precoInvalido
              ? AVISO_PRECO_INVALIDO
              : descontoInvalido
                ? AVISO_DESCONTO_INVALIDO
                : descontoZeraItem
                  ? AVISO_DESCONTO_ZERA_ITEM
                  : // Por último, pelo mesmo motivo do desconto que zera o
                    // item: a quantidade é válida, quem a recusa é o saldo
                    // (AD-236). A saída é reduzi-la.
                    (motivoSaldo ?? 'Revise quantidade, preço e desconto: há um valor inválido.');

  /**
   * O que acionar o "+" faz — pelo clique, pelo toque ou pelo TAB que chega a
   * ele vindo do desconto (`pedidosDeInsercaoPorTab`). Um handler só, para os
   * dois gestos nunca divergirem nas exceções abaixo.
   *
   * Quatro exceções ao `acaoBloqueavel`, todas porque `confirmar` faz mais do
   * que notificar:
   * - **sem vendedor**, quem responde é `exigirVendedor`, que também leva o
   *   foco ao campo do vendedor;
   * - **saldo** (AD-239), porque a confirmação reconsulta o ERP: o estoque pode
   *   ter sido reposto desde a última consulta, e repetir o motivo antigo
   *   obrigaria o operador a bipar de novo. Recusada a reconsulta, o motivo
   *   volta em toast por `comunicarSaldo`.
   * - **campo obrigatório inválido** (correção do usuário, 2026-09-17):
   *   `confirmar` passa por `previaValida`, que além de avisar leva o foco ao
   *   campo — só o toast deixava o operador sem saber onde corrigir o preço
   *   zerado.
   * - **código em consulta** (AD-254): no celular, tocar aqui com um código
   *   digitado tira o foco do campo, e é essa saída que consulta e insere. O
   *   toque chega com a consulta em voo, e anunciar "Aguarde" por cima de uma
   *   inserção que já está acontecendo seria um aviso de erro para o gesto
   *   certo. `confirmar` ignora a segunda inserção sozinho
   *   (`confirmarEntradaRapida` com `ocupado`).
   *
   * Nos quatro casos o motivo continua em `bloqueioDeInsercao`, para o botão
   * aparecer bloqueado e o `title` explicar sem depender do clique.
   */
  const acionarConfirmacao = acaoBloqueavel(
    bloqueadoPorVendedor ||
      (ocupado && semResolucao) ||
      motivoSaldo !== null ||
      (!semResolucao && (quantidadeInvalida || precoInvalido || descontoInvalido))
      ? null
      : bloqueioDeInsercao,
    confirmar,
  );

  /**
   * O TAB que sai do desconto pediu a inserção: confirma **depois** do render
   * que a saída do campo provocou.
   *
   * Num efeito, e não no próprio `keydown`: o `blur` do desconto ainda mexe na
   * barra — campo vazio vira `0,00`, desconto acima do mínimo é ajustado — e
   * esses valores só existem no render seguinte. Confirmar ali gravaria o
   * desconto de antes da saída do campo.
   *
   * Não confirma quando a saída do desconto já disse o que impede: o foco
   * preso de volta no campo (texto que não é número, `exigirCampo` no
   * `onBlur`) ou o aviso do desconto que zera o item. Nos dois o operador já
   * leu o motivo, e confirmar repetiria a mesma frase como erro.
   */
  useEffect(() => {
    if (
      pedidosDeInsercaoPorTab === 0 ||
      document.activeElement !== botaoConfirmar.current ||
      descontoZeraItem
    ) {
      return;
    }
    acionarConfirmacao();
    // Reage só ao pedido: `acionarConfirmacao` é refeita a cada render e, nas
    // dependências, confirmaria a cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidosDeInsercaoPorTab]);

  const classeRotulo = 'font-semibold text-muted-foreground';
  // Sem `flex`: um `<input>` é elemento substituído — `display:flex` nele
  // produz alinhamento inconsistente entre navegadores. A altura fixa
  // (`h-11.5`) já centraliza o texto verticalmente sozinha.
  // `h-10` no compacto, os 46px do desenho a partir de `md:`: a barra quebra em
  // cinco faixas no wizard mobile, e 6px por faixa é o que faz a etapa 1 caber
  // sem rolagem (pedido do usuário, 2026-09-09). 40px continua acima do alvo
  // mínimo de toque.
  const classeCampoValor =
    'h-10 w-full min-w-0 rounded-xl border border-border bg-muted px-sm font-mono text-md tabular-nums outline-none read-only:cursor-default disabled:cursor-not-allowed disabled:opacity-70 md:h-11.5';
  // Preço e desconto não usam `classeCampoValor`: a moldura vai para um
  // wrapper e o `<input>` fica transparente dentro dele, para o "R$" caber ao
  // lado do valor sem entrar no `value` (ver `SimboloReal`).
  const classeMolduraValor =
    'flex h-10 w-full min-w-0 items-center gap-xs rounded-xl border border-border bg-muted px-sm md:h-11.5';
  const classeValorDigitavel =
    'w-full min-w-0 bg-transparent font-mono text-md tabular-nums outline-none read-only:cursor-default';

  // `paraTextoDecimal`, não `formatarCentavos`: quem desenha o "R$" agora é o
  // `SimboloReal` ao lado, e o campo carrega só o número — o mesmo formato nos
  // dois estados, editável ou não.
  const precoExibido = editavel
    ? precoTexto
    : paraTextoDecimal(
        linhaEmEdicao?.precoUnitario ?? resolvido?.snapshot.precoBase ?? ZERO_CENTAVOS,
      );
  // Não editável mostra o desconto real da linha (convênio + manual, mesma
  // soma da coluna "Desconto" da grid) — não `0,00` fixo — quando há uma
  // linha existente carregada; numa inserção nova ainda não há desconto de
  // convênio a mostrar (`descontoConvenioFixo` é `0` nesse caso).
  const descontoExibido = editavel
    ? descontoTexto
    : paraTextoDecimal(somar(descontoConvenioFixo, linhaEmEdicao?.descontoManual ?? ZERO_CENTAVOS));

  return (
    <div
      className={cn(
        'flex flex-col gap-xs rounded-3xl border border-border bg-background p-2.5 md:p-base',
        // Contorno amarelo pulsante enquanto um item já inserido está
        // carregado aqui para edição (pedido do usuário, 2026-09-03).
        linhaEmEdicao !== null && 'cc-pulso-edicao',
      )}
      data-testid="entrada-rapida-produto"
      onKeyDown={aoTeclarNoCartao}
    >
      {/* `flex-wrap`: no desktop a linha nunca quebra (sobra largura), mas na
          etapa 1 do wizard mobile as células caem umas sob as outras em vez de
          estourar a lateral da tela — é a mesma barra, reflowada, não um
          segundo componente (`SC-001`).

          **O `flex-wrap` sozinho não quebrava nada** (achado em 2026-09-08):
          toda célula era `flex-1 min-w-0`, e um item que pode encolher até zero
          nunca chega a "não caber" — as sete se espremiam na mesma linha de
          326px, o campo de código ficava com 39px e os rótulos "Quantidade",
          "Unidade", "Preço unitário" e "Desconto do item" se sobrepunham em
          três linhas ilegíveis. O piso por célula (`min-w-*`) é o que faz a
          quebra acontecer de verdade, e reproduz o empilhamento do
          desenho mobile: código na primeira faixa com a lupa e o Scanner
          (`dfZEs`/`kU6Z5`), depois os pares de valores (`J5G7EE`/`aRe5V`) e o
          botão de inserir ocupando a largura toda (`q2NBVJ`).

          **O piso vale nos dois layouts desde 2026-09-09** (AD-198), só que com
          valores próprios em `md:`. Ele era **revogado** no desktop
          (`md:min-w-0`), e era isso que quebrava a tela: a partir do limiar as
          células voltavam a poder encolher até zero, então entre 768px e
          ~1330px o desktop reproduzia exatamente o empilhamento ilegível
          descrito acima — só que sem nunca quebrar linha.

          Os pisos do compacto são largos demais para reaproveitar aqui: somados
          aos intervalos e ao botão passam de 980px, e a barra quebrava em duas
          faixas até em 1440px, perdendo a linha única do Pencil. Os valores de
          `md:` são o rótulo mais largo de cada célula com folga, e somam ~862px:
          a faixa única aparece a partir de ~1330px — que é onde o usuário
          observou o estouro parar — e vira duas ou três faixas no monitor
          apertado, em vez de sobrepor rótulo com valor. */}
      <div
        className="flex flex-wrap items-end gap-xs md:gap-sm"
        data-testid="previa-insercao-produto"
      >
        <label className="flex min-w-[9.5rem] flex-1 flex-col gap-xxs text-sm">
          <span className="flex items-center gap-xs font-semibold text-muted-foreground">
            <Barcode className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{rotuloCampoCodigo}</span>
          </span>
          {/* `relative` para o ABC/123 ficar **dentro** da moldura do campo, na
              ponta direita: a faixa de cima já tem a lupa e o Scanner, e um
              terceiro botão ali tiraria largura do próprio código. */}
          <span className="relative flex">
            <input
              ref={campoCodigo}
              className={cn(
                'h-10 w-full rounded-xl border border-border bg-muted px-3 font-mono disabled:cursor-not-allowed disabled:opacity-70 aria-disabled:cursor-not-allowed aria-disabled:opacity-70 md:h-11.5',
                tecladoVirtual && 'pr-14',
              )}
              data-testid="campo-codigo-produto"
              {...(tecladoVirtual ? { inputMode: tecladoDoCodigo } : {})}
              /* Única exceção à regra de `FR-014` (decisão do usuário,
               2026-09-05): os atalhos globais F6–F9 disparam **com o foco
               aqui**. É onde o caixa passa a venda inteira, e obrigá-lo a sair
               do campo para fechar a venda transformaria um toque em três
               gestos. Seguro pelo mesmo motivo que a regra existe: o leitor de
               código de barras emite dígitos e `Enter`, nunca teclas de função. */
              {...ATRIBUTO_ATALHOS_PERMITIDOS}
              autoComplete="off"
              autoFocus
              placeholder="Bipe ou digite (use * p/ quantidade)"
              value={texto}
              /* **Livre na prévia, `disabled` no lápis** (correções do usuário,
               2026-09-16, nesta ordem no mesmo dia).

               Na prévia de inserção ele ficava `disabled` e o operador não
               voltava a ele nem por Tab nem com o mouse: para trocar o código
               digitado errado, só cancelando no Escape — que no compacto nem
               existe. Digitar aqui **vence** a revisão em curso, a mesma
               política que a câmera (`capturarPorCamera`) e o modal de busca já
               seguiam.

               Na edição de um item **já lançado** (lápis da grid) é o oposto: o
               código identifica a linha que está sendo alterada, e trocá-lo ali
               não teria significado — não viraria outro item, viraria o mesmo
               item com o preço e o total de outro produto. Para inserir um
               produto diferente o caminho é cancelar a edição e bipar o novo
               código, que é o gesto que o caixa já faz. */
              disabled={linhaEmEdicao !== null}
              /* **Venda congelada** (pedido do usuário, 2026-10-08): o campo
               fica `readOnly` + `aria-disabled` — apagado, com o cursor de
               proibido no hover e fora da ordem de TAB — e o clique diz por
               quê. O que já estava digitado permanece; só a digitação fecha. */
              readOnly={bloqueioDoCodigo !== null}
              {...atributosDeBloqueio(bloqueioDoCodigo)}
              title={
                linhaEmEdicao === null
                  ? (bloqueioDoCodigo ?? undefined)
                  : 'O código não muda na edição de um item já lançado: cancele com Esc para inserir outro produto.'
              }
              onClick={acaoBloqueavel(bloqueioDoCodigo, () => {
                /* campo livre: o clique só posiciona o cursor. */
              })}
              onChange={(evento) => {
                if (bloqueioDoCodigo !== null) {
                  return;
                }
                // Mexer no código é retomá-lo: redigitar o recusado e sair
                // volta a consultar, como qualquer código digitado.
                entradaRecusada.current = null;
                setTexto(evento.target.value);
              }}
              onKeyDown={aoTeclarNoCodigo}
              /* Sair do campo com o código mexido revalida no ERP
               (`aoSairDoCodigo`): sem isto, apagar o código e sair pelo TAB
               deixava preço, desconto e total do produto anterior na barra. */
              onBlur={() => {
                void aoSairDoCodigo();
              }}
            />
            {/* Pílula no vocabulário do "Scanner" do Pencil (`QIJKL`: fundo
              `$surface-strong`, raio 100, texto na cor da marca) — o desenho não
              tem controle de teclado, e este é o botão de ação pequena que ele
              já usa na mesma barra. O rótulo diz **para qual** teclado ele leva.

              `onPointerDown` com `preventDefault`: sem ele, no Android o toque
              levaria o foco para o botão e o teclado fecharia antes de reabrir
              no outro modo. Fica dentro do `<label>` do código, então o toque
              também não conta como "fora do campo" para `tecladoVirtual.ts`. */}
            {tecladoVirtual && linhaEmEdicao === null && bloqueioDoCodigo === null ? (
              <button
                type="button"
                className="absolute top-1/2 right-1.5 flex h-7 -translate-y-1/2 items-center rounded-full bg-secondary px-2.5 font-sans text-xs font-semibold text-primary"
                aria-label={
                  tecladoDoCodigo === 'numeric'
                    ? 'Usar teclado com letras'
                    : 'Usar teclado numérico'
                }
                data-testid="alternar-teclado-codigo"
                onPointerDown={(evento) => {
                  evento.preventDefault();
                }}
                onClick={alternarTecladoDoCodigo}
              >
                {tecladoDoCodigo === 'numeric' ? 'ABC' : '123'}
              </button>
            ) : null}
          </span>
        </label>

        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          className="size-10 shrink-0 rounded-full md:size-11.5"
          aria-label="Buscar produto"
          data-testid="abrir-busca-produto"
          {...atributosDeBloqueio(bloqueioDoCarrinho)}
          onClick={acaoBloqueavel(bloqueioDoCarrinho, () => {
            abrirJanela('produto');
          })}
        >
          <Search className="size-4.5" aria-hidden="true" />
        </Button>

        {/* Slot da 007: o botão "Scanner" entra aqui, ao lado da lupa, como o
            Pencil o posiciona no cabeçalho da entrada mobile (`QIJKL`). Vazio
            no desktop — e vazio também no mobile fora de Chrome/Android, porque
            quem devolve `null` é o próprio `ScannerCamera` (`FR-011`). */}
        {renderizarCaptura?.((codigo) => {
          void capturarPorCamera(codigo);
        }, bloqueioDoCarrinho)}

        {/* Única célula que **não** é um `<label>` envolvendo o campo: esta
            contém os botões +/- além do input, e o navegador aplica o
            `:hover` do label ao *labeled control* — o primeiro form control
            descendente, que aqui é o botão "−". Passar o mouse em qualquer
            ponto do campo acendia o "−" (achado do usuário, 2026-09-03).
            Com o rótulo apontando para o input por `htmlFor`, a associação
            acessível continua de pé e o hover do "−" volta a ser só o dele. */}
        {/* Sem piso próprio em `md:`, ao contrário das células de valor: os
            9.5rem são o mínimo para o par de botões −/+ ladear as três casas
            decimais da quantidade. Em 8.5rem o campo passava a mostrar "1,00"
            no lugar de "1,000" (medido em 1440px). */}
        <div className="flex min-w-[9.5rem] flex-1 flex-col gap-xxs text-sm">
          <label className={classeRotulo} htmlFor={ID_CAMPO_QUANTIDADE}>
            Quantidade
          </label>
          <div className="flex h-10 items-center justify-between gap-xs rounded-xl border border-border bg-muted px-xs md:h-11.5">
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              className="size-8 shrink-0 rounded-full bg-background"
              aria-label="Diminuir quantidade"
              data-testid="previa-quantidade-diminuir"
              /* **Venda congelada fecha a quantidade** (pedido do usuário,
               2026-10-16, AD-280): com pagamento na venda nenhum produto entra
               nem muda, e o par −/+ seguia aceso convidando a mexer numa
               quantidade que não ia a lugar nenhum. Mesmo bloqueio explicativo
               do campo de código e do lápis da grid. */
              {...atributosDeBloqueio(bloqueioDoCarrinho)}
              onClick={acaoBloqueavel(bloqueioDoCarrinho, () => {
                alterarQuantidade(-1);
              })}
            >
              <Minus className="size-4" aria-hidden="true" />
            </Button>
            <input
              ref={campoQuantidade}
              id={ID_CAMPO_QUANTIDADE}
              className="h-full w-full min-w-0 bg-transparent text-center font-mono text-lg tabular-nums outline-none aria-disabled:cursor-not-allowed aria-disabled:opacity-70"
              inputMode="decimal"
              data-testid="previa-quantidade"
              value={quantidadeTexto}
              // Fechado com a venda congelada, como o par −/+ ao lado.
              readOnly={bloqueioDoCarrinho !== null}
              {...atributosDeBloqueio(bloqueioDoCarrinho)}
              onClick={acaoBloqueavel(bloqueioDoCarrinho, () => {
                /* campo livre: o clique só posiciona o cursor. */
              })}
              onChange={(evento) => {
                if (bloqueioDoCarrinho !== null) {
                  return;
                }
                // Letra digitada no campo **não entra** (correção do usuário,
                // 2026-09-16): quantidade é número, e deixar o texto livre
                // dependia do operador reparar no aviso do `onBlur` para
                // descobrir que a barra não ia inserir nada.
                const digitado = somenteQuantidade(evento.target.value);
                avisarSeCruzouSaldo(lerQuantidadeTexto(digitado));
                setQuantidadeTexto(digitado);
                vinculo.mudarQuantidade(lerQuantidadeTexto(digitado));
              }}
              // Quantidade vazia ou zerada não sai do campo (pedido do
              // usuário, 2026-09-04). Só vale com um produto em revisão: com a
              // barra vazia nada do que está aqui entra na venda — a inserção
              // rápida pelo código usa o multiplicador do próprio código
              // ("2*7891...", `confirmarEntradaRapida`) —, e prender o foco num
              // campo que ninguém vai ler impediria o operador de bipar o
              // próximo item.
              onBlur={() => {
                if (!semResolucao && quantidadeInvalida) {
                  exigirCampo(campoQuantidade, AVISO_QUANTIDADE_INVALIDA);
                  return;
                }
                // Saldo da prévia de inserção é anunciado **aqui**, ao deixar a
                // quantidade — não ao abrir a prévia (correção do usuário,
                // 2026-09-17). Só com os demais campos em ordem: com o preço
                // zerado, o foco está saindo daqui justamente para o preço, e o
                // erro dele é o que o operador precisa ler, não o do estoque.
                if (
                  resolvido !== null &&
                  !ocupado &&
                  !precoInvalido &&
                  !descontoInvalido &&
                  !descontoZeraItem
                ) {
                  anunciarSaldo(avaliacaoSaldo);
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              className="size-8 shrink-0 rounded-full bg-background"
              aria-label="Aumentar quantidade"
              data-testid="previa-quantidade-aumentar"
              {...atributosDeBloqueio(bloqueioDoCarrinho)}
              onClick={acaoBloqueavel(bloqueioDoCarrinho, () => {
                alterarQuantidade(1);
              })}
            >
              <Plus className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <label className="flex min-w-[6rem] flex-1 flex-col gap-xxs text-sm">
          <span className={classeRotulo}>Unidade</span>
          <input
            className={cn(classeCampoValor, semResolucao && 'text-muted-foreground')}
            data-testid="previa-unidade"
            // `disabled`, não só `readOnly` (pedido do usuário, 2026-09-03): a
            // unidade vem do cadastro e o operador nunca pode alterá-la — o
            // campo some da navegação por TAB em vez de só recusar digitação.
            disabled
            value={snapshotAtivo?.unidadeMedida ?? 'UN'}
          />
        </label>

        <label className="flex min-w-[9rem] flex-1 flex-col gap-xxs text-sm md:min-w-[7rem]">
          <span className={classeRotulo}>Preço unitário</span>
          <span className={classeMolduraValor}>
            <SimboloReal testId="previa-preco-unitario-simbolo" />
            <input
              ref={campoPreco}
              className={cn(classeValorDigitavel, semResolucao && 'text-muted-foreground')}
              inputMode="decimal"
              data-testid="previa-preco-unitario"
              // `disabled`, não `readOnly` (pedido do usuário, 2026-09-11):
              // fora de `'E'` o preço vem do ERP e não há o que digitar, então
              // o campo passa a recusar o ponteiro e a sair da navegação por
              // TAB, como a unidade acima. Substitui a escolha de 2026-09-03,
              // que os mantinha alcançáveis: um campo que aceita foco mas não
              // aceita tecla é justamente o que confundia o operador ao chegar
              // num pesável pelo modal.
              disabled={!editavel}
              value={precoExibido}
              onChange={(evento) => {
                if (editavel) {
                  // Digitar de novo é um pedido novo: o ajuste anterior não vale mais.
                  descontoPedidoAntesDoMinimo.current = null;
                  vinculo.mudarPreco(evento.target.value);
                }
              }}
              // Preço vazio ou zerado não sai do campo (pedido do usuário,
              // 2026-09-04) — só onde há campo para corrigir, isto é, em
              // produto `'E'` (`precoInvalido` já embute o `editavel`).
              onBlur={() => {
                if (precoInvalido) {
                  exigirCampo(campoPreco, AVISO_PRECO_INVALIDO);
                  return;
                }
                ajustarAoMinimoEAvisar();
              }}
            />
          </span>
        </label>

        <label className="flex min-w-[9rem] flex-1 flex-col gap-xxs text-sm md:min-w-[7.5rem]">
          <span className={classeRotulo}>Desconto do item</span>
          <span className={classeMolduraValor}>
            <SimboloReal testId="previa-desconto-item-simbolo" />
            <input
              ref={campoDesconto}
              className={cn(classeValorDigitavel, semResolucao && 'text-muted-foreground')}
              inputMode="decimal"
              data-testid="previa-desconto-item"
              // Mesma troca do preço (pedido do usuário, 2026-09-11).
              disabled={!editavel}
              value={descontoExibido}
              onChange={(evento) => {
                if (editavel) {
                  descontoPedidoAntesDoMinimo.current = null;
                  vinculo.mudarDesconto(evento.target.value);
                }
              }}
              // Avisa ao **sair do campo**, não a cada tecla (pedido do
              // usuário, 2026-09-04): digitar "10,00" num item de 10,00 passa
              // por "1", "1,0"… e cada passagem dispararia um toast idêntico
              // sobre um valor que o operador ainda está escrevendo. Mesma
              // política do desconto de capa, que aplica no `blur` e no Enter.
              // O texto permanece no campo para ser corrigido; quem impede a
              // inserção é `bloqueioDeInsercao`.
              //
              // Campo vazio é desconto zero (pedido do usuário, 2026-09-24):
              // sai sem aviso e volta a mostrar `0,00`, o mesmo texto com que o
              // campo nasce. Texto que não é número vem primeiro e **prende o
              // foco**: sem número não há total a conferir, então o aviso de
              // desconto que zera o item nem chega a fazer sentido. O desconto
              // grande demais continua só avisando, sem tomar o foco — o valor
              // está escrito e o operador decide se reduz ou desiste do item.
              onBlur={() => {
                if (editavel && descontoTexto.trim() === '') {
                  vinculo.mudarDesconto(TEXTO_SEM_DESCONTO);
                  return;
                }
                if (descontoInvalido) {
                  exigirCampo(campoDesconto, AVISO_DESCONTO_INVALIDO);
                  return;
                }
                // Acima do mínimo o desconto é ajustado, e o ajustado nunca zera
                // o item: o aviso de mínimo basta.
                if (ajustarAoMinimoEAvisar()) {
                  return;
                }
                if (descontoZeraItem) {
                  notificar.aviso(AVISO_DESCONTO_ZERA_ITEM);
                }
              }}
            />
          </span>
        </label>

        <label className="flex min-w-[9rem] flex-1 flex-col gap-xxs text-sm md:min-w-[6.5rem]">
          <span className={classeRotulo}>Total item</span>
          <strong
            className={cn(
              // `whitespace-nowrap` + `overflow-hidden`: um total grande
              // (R$ 1.234.567,89) quebrava em duas linhas dentro da caixa de
              // altura fixa e transbordava para baixo (correção do usuário,
              // 2026-09-16). O rótulo já cresce com `flex-1`; quando nem assim
              // couber, o valor é cortado à direita em vez de deformar a barra.
              'flex h-10 items-center overflow-hidden rounded-xl bg-secondary px-sm font-mono text-lg whitespace-nowrap tabular-nums md:h-11.5',
              semResolucao ? 'text-muted-foreground' : 'text-primary',
            )}
            data-testid="previa-total-item"
          >
            {formatarCentavos(totalItemLido ?? ZERO_CENTAVOS)}
          </strong>
        </label>

        {/* Largura cheia no compacto, os 70px do desenho desktop a partir de
            `md:`. É o botão "Adicionar ao carrinho" do Pencil mobile (nó
            `q2NBVJ`): pílula de largura total ao pé do cartão, ícone mais
            rótulo. Um alvo de 70px perdido no fim de uma linha quebrada seria
            o gesto mais difícil da etapa justamente para a ação que o caixa
            repete a cada item.

            **Um desvio declarado**: `q2NBVJ` é preto (`$surface-dark`) e este
            botão continua na cor da marca. O cartão escuro de total já ocupa o
            topo das três etapas, e um segundo preto — este, clicável — abriria
            uma terceira cor de ação na mesma tela, ao lado do azul do "Ver
            produtos e pagamento" logo abaixo.

            O rótulo só aparece no compacto: no desktop a barra é uma linha só e
            o `aria-label` já nomeia o botão para quem usa leitor de tela. Como
            o `aria-label` vence o conteúdo no cálculo do nome acessível, o
            texto visível não muda o nome anunciado em nenhum dos dois layouts. */}
        <Button
          ref={botaoConfirmar}
          type="button"
          className="h-10 w-full shrink-0 gap-xs rounded-full md:h-11.5 md:w-[70px]"
          aria-label={
            linhaEmEdicao === null ? 'Adicionar item à venda' : 'Confirmar edição do item'
          }
          data-testid="previa-confirmar"
          {...atributosDeBloqueio(bloqueioDeInsercao)}
          // As exceções ao bloqueio explicativo estão em `acionarConfirmacao`,
          // o mesmo handler do TAB que chega aqui vindo do desconto.
          onClick={acionarConfirmacao}
        >
          <Plus className="size-5 shrink-0" aria-hidden="true" />
          <span className="text-md font-bold md:hidden">
            {linhaEmEdicao === null ? 'Adicionar ao carrinho' : 'Confirmar edição'}
          </span>
        </Button>
      </div>

      {/* `hidden md:block` quando não há produto resolvido: no desktop a linha
          continua reservando a própria altura (o espaço em branco evita que o
          cartão pule ao resolver um código), mas no wizard mobile essa reserva
          custava ~24px numa tela que precisa caber sem rolagem sem nenhum item
          (pedido do usuário, 2026-09-09). O elemento permanece no DOM nos dois
          casos — quem lê por `data-testid` continua achando. */}
      <p
        className={cn(
          'text-sm font-medium text-foreground',
          snapshotAtivo === null && 'hidden md:block',
        )}
        data-testid="previa-descricao-produto"
      >
        {snapshotAtivo?.descricao ?? ' '}
      </p>

      {/* O aviso de saldo (AD-236) **não** tem linha própria abaixo do nome do
          produto: o canal é o toast, e só ele (pedido do usuário, 2026-09-16 —
          AD-239). O motivo continua no botão bloqueado, pelo padrão de
          `lib/bloqueio.ts`, que é onde o operador o procura ao ser recusado. */}

      <ModalBuscaProduto
        aberto={buscaAberta}
        onFechar={fecharJanela}
        onProdutoSelecionado={(consulta) => {
          void selecionarDaBusca(consulta);
        }}
      />
    </div>
  );
}
